/**
 * System Status Service
 * Handles developer tools / service health endpoints
 */

import axiosInstance, { apiRequest } from '../axiosInstance';
import { SYSTEM_STATUS_ENDPOINTS } from '../endpoints';

const systemStatusService = {
  /**
   * Get .NET API server status
   * @returns {Promise<Object>} Server status info
   */
  getServerStatus: async () => {
    return apiRequest(() =>
      axiosInstance.get(SYSTEM_STATUS_ENDPOINTS.SERVER)
    );
  },

  /**
   * Get PDF service status (proxied through .NET API)
   * @returns {Promise<Object>} { reachable, statusCode, data, checkedAt }
   */
  getPdfServiceStatus: async () => {
    return apiRequest(() =>
      axiosInstance.get(SYSTEM_STATUS_ENDPOINTS.PDF_SERVICE)
    );
  },

  /**
   * Test connectivity to all external dependencies
   * @returns {Promise<Object>} { results, checkedAt }
   */
  getConnectivity: async () => {
    return apiRequest(() =>
      axiosInstance.get(SYSTEM_STATUS_ENDPOINTS.CONNECTIVITY)
    );
  },

  /**
   * Get background job statuses
   * @returns {Promise<Object>} { jobs, queriedAt }
   */
  getBackgroundJobs: async () => {
    return apiRequest(() =>
      axiosInstance.get(SYSTEM_STATUS_ENDPOINTS.BACKGROUND_JOBS)
    );
  },

  /**
   * Get PDF generation queue status
   * @returns {Promise<Object>} { reports, summary, queriedAt }
   */
  getPdfQueue: async () => {
    return apiRequest(() =>
      axiosInstance.get(SYSTEM_STATUS_ENDPOINTS.PDF_QUEUE)
    );
  },

  /**
   * Get non-secret configuration values
   * @returns {Promise<Object>} { groups, queriedAt }
   */
  getConfiguration: async () => {
    return apiRequest(() =>
      axiosInstance.get(SYSTEM_STATUS_ENDPOINTS.CONFIGURATION)
    );
  },

  /**
   * Get recent errors from in-memory ring buffer
   * @returns {Promise<Object>} { errors, totalCaptured, queriedAt }
   */
  getRecentErrors: async () => {
    return apiRequest(() =>
      axiosInstance.get(SYSTEM_STATUS_ENDPOINTS.RECENT_ERRORS)
    );
  },

  /**
   * Clear recent errors ring buffer
   * @returns {Promise<Object>} { message, clearedAt }
   */
  clearRecentErrors: async () => {
    return apiRequest(() =>
      axiosInstance.delete(SYSTEM_STATUS_ENDPOINTS.RECENT_ERRORS)
    );
  },
  /**
   * Get Sentry issues across all projects
   * @param {Object} params - Optional { project, query }
   * @returns {Promise<Object>} { issues, errors, projects, queriedAt }
   */
  getSentryIssues: async (params = {}) => {
    const queryParts = [];
    if (params.project) queryParts.push(`project=${encodeURIComponent(params.project)}`);
    if (params.query) queryParts.push(`query=${encodeURIComponent(params.query)}`);
    if (params.statsPeriod) queryParts.push(`statsPeriod=${encodeURIComponent(params.statsPeriod)}`);
    if (params.sort) queryParts.push(`sort=${encodeURIComponent(params.sort)}`);
    const qs = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    return apiRequest(() =>
      axiosInstance.get(`${SYSTEM_STATUS_ENDPOINTS.SENTRY_ISSUES}${qs}`)
    );
  },

  updateSentryIssueStatus: async (issueId, status) => {
    return apiRequest(() =>
      axiosInstance.put(SYSTEM_STATUS_ENDPOINTS.SENTRY_ISSUE_UPDATE_STATUS(issueId), { status })
    );
  },

  getSentryIssueDetails: async (issueId) => {
    return apiRequest(() =>
      axiosInstance.get(SYSTEM_STATUS_ENDPOINTS.SENTRY_ISSUE_DETAILS(issueId))
    );
  },

  getSentryIssueLatestEvent: async (issueId) => {
    return apiRequest(() =>
      axiosInstance.get(SYSTEM_STATUS_ENDPOINTS.SENTRY_ISSUE_LATEST_EVENT(issueId))
    );
  },

  getSentryIssueEvents: async (issueId) => {
    return apiRequest(() =>
      axiosInstance.get(SYSTEM_STATUS_ENDPOINTS.SENTRY_ISSUE_EVENTS(issueId))
    );
  },

  getMcpStatus: async () => {
    return apiRequest(() =>
      axiosInstance.get(SYSTEM_STATUS_ENDPOINTS.MCP_STATUS)
    );
  },
};

export default systemStatusService;
